import { facilitySelectionStore } from "../../stores/facility_selection_store";
import { useStore } from "stimulus-store";
import _ from "lodash";
import { Controller } from "@hotwired/stimulus";
import { getClosestSelectedFacilityId, getCurrentlyDisplayedFacilityType } from "../../stores/helpers/facility_helpers";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { LOCALE_TYPE_AREA } from "constants/facilities_constants";
import { areaStore } from "../../stores/locales/area_store";

/**
 * @deprecated This controller is deprecated. Use forms--locale-form-v2 instead.
 * 
 * MIGRATION GUIDE:
 * 1. Replace data-controller="forms--locale-form" with data-controller="forms--locale-form-v2"
 * 2. Use the shared partial: <%= render 'shared/locales/locale_form', locals: {...} %>
 * 3. Configure via locals instead of facilitySelectionStore
 * 4. See app/javascript/controllers/forms/README_locale_form_v2.md for full documentation
 * 
 * The v2 controller provides:
 * - Multi-context support (not just facilities table)
 * - Configurable via data attributes
 * - No dependency on facilitySelectionStore
 * - Better event handling and error management
 */

// Connects to data-controller="forms--locale-form"
export default class extends Controller {
    AREA_FIELDS_WRAPPER = 'area-fields-wrapper';

    static stores = [
        areaStore,
        facilitySelectionStore
    ];

    static targets = [
        "floorInput",
        "floorPlanName",
        "floorPlanPreviewPlaceholder",
        "floorPlanPreview",
        "form",
        "idInput",
        "managerInput",
        "modal",
        "nameInput",
        "placeholder",
        "submit",
        "title"
    ]
    static values = {
        method: String,
        url: String
    }

    initialize() {
        useStore(this);
        
        // Deprecation warning
        console.warn(
            '⚠️  DEPRECATED: forms--locale-form controller is deprecated.\n' +
            '   Please migrate to forms--locale-form-v2.\n' +
            '   See app/javascript/controllers/forms/README_locale_form_v2.md for migration guide.'
        );
    }

    handleAddButtonClick(event) {
        // Clear out the inputs that may be dirty
        this._reenableSubmitButton();
        this._clearInputs()
        this._removeAreaFields();

        // Determine what locale type is selected
        const selectedLocaleTypeName = getCurrentlyDisplayedFacilityType(this.facilitySelectionStoreValue);

        // Set the modal's values. These will be used when saving the locale.
        this.methodValue = 'post'
        this.urlValue = `/api/locales/${_.toLower(selectedLocaleTypeName)}s/create`

        // Set the modal's title
        this.titleTarget.textContent = this._getTitle('Create');

        // Add the area fields if the user is adding an area
        if (selectedLocaleTypeName === LOCALE_TYPE_AREA) {
            this._addAreaFields();
        }

        // Open the modal
        $(this.modalTarget).modal('show')
    }

    handleUpdateButtonClick(event) {
        // Clear out the inputs that may be dirty
        this._reenableSubmitButton();
        this._clearInputs()
        this._removeAreaFields();

        // Determine what locale type is selected
        const selectedLocaleTypeName = getCurrentlyDisplayedFacilityType(this.facilitySelectionStoreValue);

        // Set the modal's values. These will be used when saving the locale.
        this.methodValue = 'patch'
        this.urlValue = `/api/locales/${_.toLower(selectedLocaleTypeName)}s/update`

        // Set the modal's title
        this.titleTarget.textContent = this._getTitle('Update');

        // Get this row's ID element
        const localeId = event.detail.id;
        const localeType = event.detail.localeType;
        const localeName = event.detail.name;

        // Set the form's ID and name
        this.idInputTarget.value = localeId;
        this.nameInputTarget.value = localeName;

        // Add the area fields if the user is adding an area
        if (selectedLocaleTypeName === LOCALE_TYPE_AREA) {
            this._addAreaFields();
            this._addExistingFloorPlanPreviewFields(localeId);
            this._populateAreaFields(localeId);
        }

        // Open the modal
        $(this.modalTarget).modal('show');
    }

    handleSubmit(event) {
        // Prevent the default event operation from running
        event.preventDefault();

        // Grab the route and method to call
        const url = _.toLower(this.urlValue);
        const method = _.toUpper(this.methodValue);

        // Grab the form data
        // const submitter = this.submitTarget;
        const formData = new FormData(event.target, event.submitter);

        // Determine what locale type is selected and grab its ID
        const parentId = getClosestSelectedFacilityId(this.facilitySelectionStoreValue);

        // Mix the soon-to-be-created locale's parent's ID into the form data.
        if (parentId !== null && !_.isEmpty(parentId)) {
            formData.set('parent_id', parentId);
        }

        // Issue the request
        $.ajax({
            url: url,
            method: method,
            dataType: 'json',
            format: 'json',
            data: formData,

            // This prevents jQuery from processing the FormData.
            // We need to do this to mix in additional values using FormData.append(...)
            processData: false,

            // Settings this to false lets the browser set the content type. This
            // is needed to mix in additional values using FormData.append(...)
            contentType: false,
        }).promise().then((result) => {
            const {name, id, localeType} = result;

            // Add the newly created item to the table
            // addItemToTable(localeType, id, name);

            // Create the event name. This determines how the new or updated locale is added/updated in the table.
            const eventName = `locale${_.toLower(method) === 'post' ? 'Created' : 'Updated'}`;

            // Create the details hash. We're doing this here to mix in additional fields if this is an area

            // Mix in those area attributes if this is an area
            if (localeType === LOCALE_TYPE_AREA) {
                this._updateAreaStore(result)
            }

            // Dispatch the event
            window.dispatchEvent(new CustomEvent(eventName, {
                detail: {
                    localeType: localeType,
                    id: id,
                    name: name
                },
                bubbles: true // This needs to be explicitly set to enable event bubbling
            }));

            // Tell the user their request was successful
            sendSuccessNotification(`Successfully ${_.toLower(method) === 'post' ? 'created' : 'updated'} ${localeType} ${name}`);

            // Clear the inputs so the modal isn't dirty the next time someone clicks "add" or "edit"
            this._clearInputs();

            // This is hacky but for some reason the submit button is being disabled on submission.
            this._reenableSubmitButton();

            // Remove the area fields in case the next time the user opens this they aren't modifying an area
            this._removeAreaFields();

            // Close the modal
            $(this.modalTarget).modal('hide');
        }).catch((error) => {
            // Determine what locale type is selected
            const selectedLocaleTypeName = getCurrentlyDisplayedFacilityType(this.facilitySelectionStoreValue);

            // Tell the user their request failed
            sendFailureNotification(`Failed to ${_.toLower(method) === 'post' ? 'create' : 'update'} a ${selectedLocaleTypeName}.\n${error.responseJSON.message}`);

            // Clear the inputs so the modal isn't dirty the next time someone clicks "add" or "edit"
            this._clearInputs();

            // This is hacky but for some reason the submit button is being disabled on submission.
            this._reenableSubmitButton();

            // Remove the area fields in case the next time the user opens this they aren't modifying an area
            this._removeAreaFields();

            // Close the modal
            $(this.modalTarget).modal('hide');
        });
    }

    _getTitle(action) {
        const selectedFacilities = this.facilitySelectionStoreValue;

        if (_.isEmpty(selectedFacilities.region.id)) {
            return `${_.capitalize(action)} region`;
        } else if (_.isEmpty(selectedFacilities.building.id)) {
            return `${_.capitalize(action)} building`;
        } else if (_.isEmpty(selectedFacilities.area.id)) {
            return `${_.capitalize(action)} area`;
        } else if (_.isEmpty(selectedFacilities.room.id)) {
            return `${_.capitalize(action)} room`;
        } else {
            return `${_.capitalize(action)} room`;
        }
    }

    _addAreaFields() {
        // Get the placeholder div. We'll add the new fields here.
        const placeholderDiv = this.placeholderTarget;

        /*
         * I'm doing this in raw HTML in Javascript instead of in a Rails partial or a data-template
         * since those methods require issuing a remote request to the controller to render the partial
         * or having these fields stubbed out but hidden in the form.
         */
        const container = document.createElement('div');
        container.classList.add(this.AREA_FIELDS_WRAPPER);
        container.innerHTML = `
            <div class="form-group mt-3">
                <label class="form-label" for="floor">Floor</label>
                <input placeholder="Enter floor" class="form-control mt-2" type="text" name="floor" id="floor" data-forms--locale-form-target="floorInput">
            </div>
            <div class="form-group mt-3">
                <label class="form-label" for="manager">Manager</label>
                <input placeholder="Enter manager" class="form-control mt-2" type="text" name="manager" id="manager" data-forms--locale-form-target="managerInput">
            </div>
            <div class="form-group mb-3">
                <label class="form-label" for="fileUpload">Floor plan</label>
                <input class="form-control area-form-floorplan form-control-file custom-file-input mt-2" id="fileUpload" type="file" name="floor_plan">
                <div class="form-text text-muted">Accepted formats: PDF, SVG, JPEG, PNG, WebP</div>
            </div>
            <div class="form-group mb-3">
                <div data-forms--locale-form-target="floorPlanPreviewPlaceholder"></div>
            </div>
          `;

        placeholderDiv.appendChild(container);
    }

    _addExistingFloorPlanPreviewFields(id) {
        // Get the placeholder div. We'll add the new fields here.
        const placeholderDiv = this.floorPlanPreviewPlaceholderTarget;

        // Check if this area currently has a floor plan assigned
        const areaMetadata = this.areaStoreValue[id];
        const hasFloorPlan = !!(areaMetadata && areaMetadata.floor_plan !== null);

        // Check if this area currently has a floor plan assigned
        // const hasFloorPlan = this.areaStoreValue[id].floor_plan !== null;

        /*
         * I'm doing this in raw HTML in Javascript instead of in a Rails partial or a data-template
         * since those methods require issuing a remote request to the controller to render the partial
         * or having these fields stubbed out but hidden in the form.
         */
        const container = document.createElement('div');

        if (hasFloorPlan) {
            container.innerHTML = `
                <div class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
                      <!-- Photo Preview Section -->
                      <div class="card shadow-sm border-0" style="max-width: 300px;">
                        <div class="card-body text-center">
                          <!-- Preview Image -->
                          <img id="floor-plan-preview" 
                               src="" 
                               alt="Floor plan" 
                               class="img-fluid rounded mb-2"
                               style="max-height: 150px; object-fit: cover;"
                               data-forms--locale-form-target="floorPlanPreview" 
                               />
                    
                          <!-- File Name -->
                          <p class="text-muted small">
                            <i class="bi bi-file-earmark-image"></i>
                            <span id="floor-plan-name" data-forms--locale-form-target="floorPlanName"></span>
                          </p>
                        </div>
                      </div>
                    </div>
                </div>
            `;
        } else {
            container.innerHTML = `
                <div class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
                      <!-- Photo Preview Section -->
                      <div class="card shadow-sm border-0" style="max-width: 300px;">
                        <div class="card-body text-center">
                            <div class="d-flex justify-content-center align-items-center bg-secondary text-white rounded" 
                                 style="width: 300px; height: 200px;">This area does not have an  assigned floor plan</div>
                        </div>
                      </div>
                    </div>
                </div>
            `;
        }

        placeholderDiv.appendChild(container);
    }

    /**
     * This function updates the areaStore with the new area metadata when areas are updated
     * using the Update Area form or adds newly created areas when areas. It's necessary to
     * persist area metadata client side since we don't embed the non-name/ID information in
     * the Facilities Table and I don't want to issue another GET request to grab that to
     * populate the Update Area form.
     *
     * @param newOrUpdatedArea
     * @private
     */
    _updateAreaStore(newOrUpdatedArea) {
        // Const grab the new or updated area's ID
        const id = newOrUpdatedArea.id;

        // Grab the areas that have been fetched from the server
        const areas = this.areaStoreValue;

        // Grab the current area's metadata from the area store. This will only
        // be present when the user updated an existing area.
        let area = areas[id]

        // Check if this area exists. If it doesn't, return an empty hash.
        if (area === undefined) {
            area = {}
        }

        // Update the new or updated area metadata
        area.name = newOrUpdatedArea.name;
        area.floor = newOrUpdatedArea.floor;
        area.floor_plan = newOrUpdatedArea.floor_plan;
        area.floor_plan_url = newOrUpdatedArea.floor_plan_url;
        area.manager = newOrUpdatedArea.manager;

        // Put or replace the existing area at this id
        areas[id] = area;

        // Update the actual store value
        this.setAreaStoreValue(areas);
    }

    _populateAreaFields(id) {
        const areaMetadata = this.areaStoreValue[id];

        this.floorInputTarget.value = areaMetadata.floor;
        this.managerInputTarget.value = areaMetadata.manager;

        if (areaMetadata.floor_plan !== null && areaMetadata.floor_plan !== undefined && !_.isEmpty(areaMetadata.floor_plan)) {
            const floorPlanMetadata = JSON.parse(areaMetadata.floor_plan).metadata;
            const floorPlanName = floorPlanMetadata.filename;
            const floorPlanUrl = areaMetadata.floor_plan_url;

            // Add the existing floor plan's filename
            this.floorPlanNameTarget.textContent = floorPlanName;

            // Every stored floor plan is a directly-displayable image
            // (WebP/SVG); PDFs are converted server-side on upload, so the
            // preview <img> src is set directly from the floor plan URL.
            this.floorPlanPreviewTarget.src = floorPlanUrl;
        }
    }

    _clearInputs() {
        // The submit button is actually an input element. We have to be careful with how
        // we clear inputs or we'll bash the "Submit" value. Using the form-control class
        // is good enough.
        document.querySelectorAll('input.form-control').forEach(input => {
            input.value = '';
        });
    }

    /**
     * This is hacky but for some reason the value of the submit button is being cleared.
     * Let's reset it so this form is usable again
     * @private
     */
    _reenableSubmitButton() {
        this.submitTarget.value = 'Submit';
        this.submitTarget.disabled = false;
    }

    _removeAreaFields() {
        this.placeholderTarget.replaceChildren();
    }

    _removeFloorPlanPreviewFields() {
        this.floorPlanPreviewTarget.replaceChildren();
    }

}